import {
  ADDRESSABLE_STATUSES,
  CONVERSATION_TITLE_MAX,
  MESSAGE_TEXT_MAX,
  type ChatMessage,
  type ConversationSummary,
  type Employee,
  type EmployeeStatus,
  type RunStatus,
} from '@coworker/contracts';
import { type FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router';

import { RunTimeline } from '@/components/run-timeline.js';
import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { errorText } from '@/lib/api-error.js';
import { useConversations, useCreateConversation } from '@/hooks/use-conversations.js';
import { useCurrentWorkspace } from '@/hooks/use-current-workspace.js';
import { useApprovals, useDecideApproval } from '@/hooks/use-approvals.js';
import { useEmployees } from '@/hooks/use-employees.js';
import { useMessages, useRun, useSendMessage, useThreadRuns } from '@/hooks/use-messages.js';
import { isTerminalRunStatus } from '@coworker/contracts';

/**
 * Chat - WEB-007, with the employee selector of EMP-009.
 *
 * **A thread is addressed to one employee, for its whole life.** The selector is
 * live while a chat is being opened and read-only afterwards; changing who you
 * are talking to starts a new chat. `thread.employee_id` is a single column and
 * a message row carries no employee of its own, so a rebound thread would be a
 * transcript nobody could attribute - ADR-0037.
 *
 * **The employee answers, and the answer is not part of the send's reply.**
 * A message starts a run; the run writes the answer into the thread when it
 * finishes. So this screen polls the run and re-reads the thread once, which is
 * also the shape a streaming transport would replace without changing the
 * response - ADR-0044.
 *
 * A `NOT_CONNECTED` row is history: the route stopped writing them when the
 * model was connected, and the rows already in the database still render as a
 * notice rather than as something the employee said.
 */

/**
 * Widened from the literal tuple, because the tuple's own `includes` refuses
 * every status outside it - which is the question being asked.
 * `packages/domain/src/domain.spec.ts` holds this list to `canRun`.
 */
const ADDRESSABLE: readonly EmployeeStatus[] = ADDRESSABLE_STATUSES;

function isAddressable(employee: Employee): boolean {
  return ADDRESSABLE.includes(employee.status);
}

/** The API never generates a title and the column defaults to the empty string. */
function threadTitle(conversation: ConversationSummary): string {
  return conversation.title.trim() === '' ? 'Untitled chat' : conversation.title;
}

const INPUT_CLASS = 'rounded-[--radius] border border-border bg-transparent px-3 py-2 text-sm';

export function Chat() {
  const workspace = useCurrentWorkspace();
  const { query } = useConversations();
  const [threadId, setThreadId] = useState<string | undefined>(undefined);

  return (
    <section className="mx-auto flex max-w-5xl flex-col gap-6">
      <h1 className="text-2xl font-semibold">Chat</h1>

      {workspace === undefined ? (
        // A real state, not a loading one: the session carries INVITED and
        // SUSPENDED memberships too, and neither authorizes anything.
        <p className="text-sm text-muted-foreground">
          You are not an active member of any workspace yet. Once an invitation is accepted, your
          chats appear here.
        </p>
      ) : (
        // One column on a phone, two from `md` up. The list is above the
        // transcript rather than beside it, which is the only layout decision a
        // narrow screen needs here.
        <div className="grid gap-6 md:grid-cols-[16rem_1fr]">
          <ThreadList
            query={query}
            selected={threadId}
            onSelect={setThreadId}
            onNew={() => setThreadId(undefined)}
          />
          {threadId === undefined ? (
            <NewChat onCreated={setThreadId} />
          ) : (
            // **`key` so switching threads discards the run being polled.**
            // Without it the component instance survives, and thread A's run
            // keeps polling under thread B - rendering A's status beneath B's
            // transcript and refetching the wrong thread when it settles.
            <Thread
              key={threadId}
              threadId={threadId}
              conversation={query.data?.conversations.find((item) => item.id === threadId)}
            />
          )}
        </div>
      )}
    </section>
  );
}

interface ThreadListProps {
  readonly query: ReturnType<typeof useConversations>['query'];
  readonly selected: string | undefined;
  readonly onSelect: (threadId: string) => void;
  readonly onNew: () => void;
}

function ThreadList({ query, selected, onSelect, onNew }: ThreadListProps) {
  return (
    <nav aria-label="Chats" className="flex flex-col gap-3">
      <Button variant="outline" size="sm" onClick={onNew} className="self-start">
        + New chat
      </Button>
      <ThreadListBody query={query} selected={selected} onSelect={onSelect} />
    </nav>
  );
}

function ThreadListBody({ query, selected, onSelect }: Omit<ThreadListProps, 'onNew'>) {
  if (query.isPending) {
    return (
      <p role="status" className="text-sm text-muted-foreground">
        Loading your chats…
      </p>
    );
  }

  if (query.isError) {
    return (
      <div role="alert" className="flex flex-col items-start gap-3">
        <p className="text-sm text-destructive">{errorText(query.error)}</p>
        <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  const { conversations } = query.data;

  if (conversations.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No chats yet. Start one and it will be listed here.
      </p>
    );
  }

  return (
    <ul className="flex flex-col gap-1">
      {conversations.map((conversation) => (
        <li key={conversation.id}>
          <button
            type="button"
            aria-current={conversation.id === selected}
            onClick={() => onSelect(conversation.id)}
            className="w-full rounded-[--radius] px-3 py-2 text-left text-sm hover:bg-muted aria-[current=true]:bg-muted"
          >
            {threadTitle(conversation)}
          </button>
        </li>
      ))}
    </ul>
  );
}

interface NewChatProps {
  readonly onCreated: (threadId: string) => void;
}

/**
 * Opening a chat, and choosing who it is addressed to - EMP-009.
 *
 * The default is the Generalist. It is not remembered between chats, and that
 * is structural rather than a reset somebody has to remember to call: this
 * panel is unmounted the moment a thread is selected, so `+ New chat` always
 * mounts it fresh. The test named for that criterion drives the real path.
 */
function NewChat({ onCreated }: NewChatProps) {
  const { query } = useEmployees();

  if (query.isPending) {
    return (
      <p role="status" className="text-sm text-muted-foreground">
        Loading your employees…
      </p>
    );
  }

  if (query.isError) {
    return (
      <div role="alert" className="flex flex-col items-start gap-3">
        <p className="text-sm text-destructive">{errorText(query.error)}</p>
        <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  const { employees } = query.data;
  const addressable = employees.filter(isAddressable);
  // The Generalist unless it cannot take work, in which case anyone who can.
  const preferred = addressable.find((one) => one.employeeType === 'GENERALIST') ?? addressable[0];

  if (preferred === undefined) {
    return (
      <div className="flex flex-col items-start gap-3">
        <p className="text-sm text-muted-foreground">
          No employee in this workspace can take work right now. An employee has to be on probation
          or active before a chat can be addressed to it.
        </p>
        <Button variant="outline" size="sm" asChild>
          <Link to="/employees">Go to Employees</Link>
        </Button>
      </div>
    );
  }

  return <NewChatForm employees={employees} preferred={preferred} onCreated={onCreated} />;
}

interface NewChatFormProps extends NewChatProps {
  readonly employees: readonly Employee[];
  readonly preferred: Employee;
}

function NewChatForm({ employees, preferred, onCreated }: NewChatFormProps) {
  const create = useCreateConversation();
  const [selected, setSelected] = useState<string | undefined>(undefined);
  const [title, setTitle] = useState('');

  const employeeId = selected ?? preferred.id;

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    create.mutate(
      {
        employeeId,
        ...(title.trim() === '' ? {} : { title: title.trim() }),
      },
      { onSuccess: (created) => onCreated(created.conversation.id) },
    );
  }

  return (
    <form onSubmit={onSubmit} className="flex max-w-md flex-col gap-3">
      <h2 className="text-lg font-semibold">Start a chat</h2>
      <p className="text-sm text-muted-foreground">
        Who you address it to is fixed for the life of the chat. To talk to somebody else, start
        another one.
      </p>

      <label className="flex flex-col gap-1 text-sm">
        Employee
        <select
          value={employeeId}
          onChange={(event) => setSelected(event.target.value)}
          className={INPUT_CLASS}
        >
          {employees.map((employee) => (
            <option key={employee.id} value={employee.id} disabled={!isAddressable(employee)}>
              {/* An employee who cannot take work is shown and refused rather
                  than hidden, so a person sees why their choice is missing. */}
              {employee.name} — {employee.roleTitle}
              {isAddressable(employee) ? '' : ` (${employee.status})`}
            </option>
          ))}
        </select>
      </label>

      <label className="flex flex-col gap-1 text-sm">
        Title (optional)
        <input
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          maxLength={CONVERSATION_TITLE_MAX}
          className={INPUT_CLASS}
        />
      </label>

      {create.isError ? (
        // The API's own sentence. A refused employee is a 409 whose message
        // names the employee and the status it is in, which is more than this
        // screen could say without restating a rule it does not own.
        <p role="alert" className="text-sm text-destructive">
          {errorText(create.error)}
        </p>
      ) : null}

      <Button type="submit" disabled={create.isPending} className="self-start">
        {create.isPending ? 'Starting…' : 'Start chat'}
      </Button>
    </form>
  );
}

interface ThreadProps {
  readonly threadId: string;
  readonly conversation: ConversationSummary | undefined;
}

function Thread({ threadId, conversation }: ThreadProps) {
  const { query } = useMessages(threadId);
  // **The run the last send started.** The answer is written by the runtime, so
  // the transcript only learns about it by being asked again; `useRun` polls
  // until the run stops and this refetches once when it does.
  const [runId, setRunId] = useState<string | undefined>(undefined);
  // **Recovered on mount, because state does not survive a reload.** A run that
  // answers in seconds could afford to be forgotten; one parked on an approval
  // cannot - the card is in the thread and the buttons would work, but nothing
  // would notice when the run carried on. `RunRepository.listForThread` was
  // written for exactly this and had no caller until now.
  const liveRun = useThreadRuns(threadId);
  // A live run first, then the newest finished one. `listForThread` orders by
  // `created_at desc`, so `runs[0]` is the last thing this thread did.
  //
  // **The finished one costs a single request, not a poll**: `useRun` returns
  // `false` from `refetchInterval` as soon as the status is terminal. Without
  // it the timeline exists only until the tab is reloaded - §21.3 asks for it
  // to stay available for audit, and the steps are in the database either way.
  const activeRunId =
    runId ??
    liveRun.data?.runs.find((one) => !isTerminalRunStatus(one.status))?.id ??
    liveRun.data?.runs[0]?.id;
  const run = useRun(activeRunId);
  const status = run.data?.run.status;
  const employees = useEmployees();

  // **Refetch when the run stops - and when it stops to ask.** The answer is
  // written by the runtime into the same thread this query reads, so nothing in
  // the cache knows about it. Keyed on the status rather than on every poll, so
  // a run that takes ten seconds costs one extra read and not five.
  //
  // `WAITING_FOR_APPROVAL` had to join the terminal statuses here, and without
  // it D7b does not work at all: the card is written *during* the run, and a
  // transcript that only re-reads at the end would never show the question
  // somebody is being asked.
  const refetch = query.refetch;
  useEffect(() => {
    if (status === undefined) return;
    if (!isTerminalRunStatus(status) && status !== 'WAITING_FOR_APPROVAL') return;
    void refetch();
  }, [status, refetch]);
  const employee = employees.query.data?.employees.find(
    (one) => one.id === conversation?.employeeId,
  );

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-center gap-2 border-b border-border pb-3">
        <h2 className="text-lg font-semibold">
          {conversation === undefined ? 'Chat' : threadTitle(conversation)}
        </h2>
        {/* Read-only on purpose - ADR-0037. There is no control here because
            there is no request that would change it. */}
        <span className="text-sm text-muted-foreground">
          Addressed to {employee === undefined ? 'this employee' : employee.name}
        </span>
      </header>

      <Transcript query={query} employeeName={employee?.name} />
      <RunTimeline
        steps={run.data?.steps}
        runStatus={status}
        instruction={run.data?.run.instructionSnapshot}
      />
      <RunNotice status={status} runId={activeRunId} startedHere={runId !== undefined} />
      <Composer threadId={threadId} onStarted={setRunId} />
    </div>
  );
}

interface TranscriptProps {
  readonly query: ReturnType<typeof useMessages>['query'];
  readonly employeeName: string | undefined;
}

function Transcript({ query, employeeName }: TranscriptProps) {
  if (query.isPending) {
    return (
      <p role="status" className="text-sm text-muted-foreground">
        Loading this chat…
      </p>
    );
  }

  if (query.isError) {
    return (
      <div role="alert" className="flex flex-col items-start gap-3">
        <p className="text-sm text-destructive">{errorText(query.error)}</p>
        <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  const { messages } = query.data;

  if (messages.length === 0) {
    return <p className="text-sm text-muted-foreground">Nothing has been said yet.</p>;
  }

  return (
    <ol aria-label="Messages" className="flex flex-col gap-4">
      {messages.map((message) => (
        <li key={message.id}>
          <Message message={message} employeeName={employeeName} />
        </li>
      ))}
    </ol>
  );
}

interface MessageProps {
  readonly message: ChatMessage;
  readonly employeeName: string | undefined;
}

function Message({ message, employeeName }: MessageProps) {
  // **The employee asking, not the employee talking** - AGT-008. §14.3: the UI
  // must show what is about to happen, not "somebody wants permission", so the
  // card carries the app, the action and which fields were filled. The values
  // are the customer's and are deliberately not here.
  //
  // First, before the `NOT_CONNECTED` branch, because this is the one kind a
  // person has to *act* on and a fall-through would render it as prose with two
  // buttons missing.
  if (message.content.kind === 'APPROVAL') {
    return <ApprovalRequest content={message.content} />;
  }

  // The assertion this screen exists to make: a placeholder is a notice about
  // the product, not something an employee said. It carries no author, and it
  // is styled as an aside rather than as a reply.
  if (message.content.kind === 'NOT_CONNECTED') {
    return (
      <div
        role="note"
        className="flex flex-col gap-1 rounded-[--radius] border border-dashed border-border bg-muted/40 p-3"
      >
        <Badge variant="muted" className="self-start">
          Not connected
        </Badge>
        <p className="text-sm text-muted-foreground">{message.content.text}</p>
      </div>
    );
  }

  const author = message.senderType === 'USER' ? 'You' : (employeeName ?? 'This employee');

  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-muted-foreground">{author}</span>
      <p className="text-sm whitespace-pre-wrap">{message.content.text}</p>
    </div>
  );
}

/**
 * The card an employee asks with, in the thread where it asked.
 *
 * **Append-only, so a decision is a new message rather than a repaint.** The
 * card does not change when it is answered; the next thing in the thread says
 * what happened. That is why the buttons disappear once the request is no
 * longer pending rather than turning into a state on this row - the row is
 * history, and history does not update.
 */
function ApprovalRequest({
  content,
}: {
  readonly content: Extract<ChatMessage['content'], { kind: 'APPROVAL' }>;
}) {
  const { query } = useApprovals();
  const decide = useDecideApproval();
  const expired = Date.parse(content.expiresAt) <= Date.now();
  // **Three states, not two.** A first version derived "still pending" from
  // membership in this list alone, so a list that had not loaded - or had
  // errored - printed "This has been dealt with." about an open request, with
  // no way to act and no error shown. `undefined` means we do not know yet.
  const pending =
    query.data === undefined
      ? undefined
      : query.data.approvals.some((one) => one.id === content.approvalId);

  return (
    <div
      role="note"
      className="flex flex-col gap-2 rounded-[--radius] border border-border bg-muted/40 p-3"
    >
      <div className="flex flex-wrap items-baseline gap-2">
        <Badge variant="outline" className="self-start">
          Needs your permission
        </Badge>
        <span className="text-xs text-muted-foreground">
          {content.sideEffect.toLowerCase().replace(/_/g, ' ')}
        </span>
      </div>

      <p className="text-sm">{content.text}</p>

      {decide.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {errorText(decide.error)}
        </p>
      ) : null}

      {query.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {errorText(query.error)}
        </p>
      ) : null}

      {pending === undefined ? (
        <p className="text-xs text-muted-foreground">Checking whether this is still open…</p>
      ) : expired ? (
        <p className="text-xs text-muted-foreground">
          This expired. Ask again if you still want it.
        </p>
      ) : !pending ? (
        <p className="text-xs text-muted-foreground">
          {/* Answered, withdrawn, or answered from another session. The thread
              says what happened next; this row does not need to guess. */}
          This has been dealt with.
        </p>
      ) : (
        <div className="flex gap-2">
          <Button
            type="button"
            disabled={decide.isPending}
            onClick={() => decide.mutate({ id: content.approvalId, decision: 'APPROVED' })}
          >
            Allow once
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={decide.isPending}
            onClick={() => decide.mutate({ id: content.approvalId, decision: 'REJECTED' })}
          >
            Don&apos;t
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * What the employee is doing, or why it did not answer.
 *
 * **The failure carries the run id.** Without it the first tests have a chat
 * that says nothing happened and no way to find out why; with it, one
 * `GET /v1/runs/{id}` shows the step that failed and its code.
 */
function RunNotice({
  status,
  runId,
  startedHere,
}: {
  readonly status: RunStatus | undefined;
  readonly runId: string | undefined;
  /** Did this tab send the message that started it, or was it found on mount? */
  readonly startedHere: boolean;
}) {
  if (runId === undefined || status === undefined) return null;
  if (status === 'COMPLETED') return null;

  // **A terminal run this tab did not start says nothing.** Every sentence
  // below is written about the message you just sent - "this message was not
  // answered" - and AGT-014 made a finished run reachable on mount, so without
  // this a thread whose last run failed a week ago would greet you with an
  // assertive alert about it on every open, restated under a transcript that
  // has moved on. The timeline carries that history instead, which is what
  // §21.3 means by "available for audit": there when you look, not shouted.
  //
  // A run that is still going speaks either way - a request parked on an
  // approval has to say so after a reload, which is what D7b's recovery is for.
  if (!startedHere && isTerminalRunStatus(status)) return null;

  if (status === 'FAILED' || status === 'CANCELLED' || status === 'POLICY_BLOCKED') {
    return (
      <p role="alert" className="text-sm text-destructive">
        This message was not answered ({status.toLowerCase()}). Run {runId}.
      </p>
    );
  }

  // **`BUDGET_EXCEEDED` is terminal, and it used to land here.** It fell
  // through to "Working on it…" while the polling had already stopped, so the
  // chat said the employee was still working on a message it had given up on -
  // for as long as the tab stayed open. Unreachable until D7a-2 gave the loop a
  // ceiling, which is why nobody had seen it.
  // **Waiting for a person, not working.** Without this the run reads as
  // "Working on it…" while it is in fact stopped and asking - and the poll
  // never ends, because `WAITING_FOR_APPROVAL` is not terminal. The card with
  // the buttons is in the transcript above.
  if (status === 'WAITING_FOR_APPROVAL') {
    return (
      <p role="status" className="text-sm text-muted-foreground">
        Waiting for you to answer the request above.
      </p>
    );
  }

  if (status === 'BUDGET_EXCEEDED') {
    return (
      <p role="status" className="text-sm text-muted-foreground">
        That one needed more steps than a single message allows, so it stopped part-way. Run {runId}
        .
      </p>
    );
  }

  return (
    <p role="status" className="text-sm text-muted-foreground">
      Working on it…
    </p>
  );
}

function Composer({
  threadId,
  onStarted,
}: {
  readonly threadId: string;
  readonly onStarted: (runId: string) => void;
}) {
  const send = useSendMessage(threadId);
  const [text, setText] = useState('');

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    send.mutate(text.trim(), {
      onSuccess: (sent) => {
        setText('');
        onStarted(sent.runId);
      },
    });
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-2 border-t border-border pt-4">
      <label className="flex flex-col gap-1 text-sm">
        Message
        <textarea
          value={text}
          onChange={(event) => setText(event.target.value)}
          rows={3}
          maxLength={MESSAGE_TEXT_MAX}
          className={INPUT_CLASS}
        />
      </label>

      {send.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {errorText(send.error)}
        </p>
      ) : null}

      <Button type="submit" disabled={send.isPending || text.trim() === ''} className="self-start">
        {send.isPending ? 'Sending…' : 'Send'}
      </Button>
    </form>
  );
}
